import {FC} from 'react';
import capitalize from 'lodash/capitalize';

import {ColorLabel} from '@shared/components';
import {Color} from '@shared/types';

import {
  COLOR__BACK,
  COLOR__GRAY,
  COLOR__PRIMARY,
  COLOR__PRIMARY_LIGHT,
  COLOR__SUCCESS_LIGHT,
  COLOR__SUCCESS_SOFT_DARK,
  COLOR__WARNING,
} from '@/theme/colors';

interface TypeAttrs {
  label: string;
  bgColor: Color;
  textColor: Color;
}

// The keys are backend-specific (TransactionTypeResponseEnum).
// Labels and colors are aligned with the mobile app.
const txnType2Attrs: Record<string, TypeAttrs> = {
  order_hold: {
    label: 'Hold',
    bgColor: '#FEF3E1',
    textColor: COLOR__WARNING,
  },
  order_hold_released: {
    label: 'Unhold',
    bgColor: COLOR__PRIMARY_LIGHT,
    textColor: COLOR__PRIMARY,
  },
  order_cancel_released: {
    label: 'Unhold (Cancel)',
    bgColor: COLOR__PRIMARY_LIGHT,
    textColor: COLOR__PRIMARY,
  },
  order_settled: {
    label: 'Settled',
    bgColor: COLOR__SUCCESS_LIGHT,
    textColor: COLOR__SUCCESS_SOFT_DARK,
  },
  order_exit: {
    label: 'Sold',
    bgColor: '#E1FBF9',
    textColor: '#1FB0AB',
  },
  exit_fee: {
    label: 'Fee (Exit)',
    bgColor: '#FFE8F6',
    textColor: '#F04BA6',
  },
  order_result: {
    label: 'Resolved',
    bgColor: COLOR__BACK,
    textColor: COLOR__GRAY,
  },
  result_fee: {
    label: 'Fee (Result)',
    bgColor: '#FFE8F6',
    textColor: '#F04BA6',
  },
  order_refund: {
    label: 'Refunded',
    bgColor: '#E9F6F6',
    textColor: '#434C4B',
  },
  cfp_compensation: {
    label: 'CFP Compensation',
    bgColor: '#FFE8F6',
    textColor: '#F04BA6',
  },
};

// Payments have no `txn_type`, their `payment_type` is shown instead
const getPaymentAttrs = (paymentType: string): TypeAttrs => ({
  label: capitalize(paymentType),
  bgColor: COLOR__SUCCESS_LIGHT,
  textColor: COLOR__SUCCESS_SOFT_DARK,
});

const getFallbackAttrs = (txnType: string): TypeAttrs => ({
  label: capitalize(txnType.replace(/_/g, ' ')),
  bgColor: COLOR__BACK,
  textColor: COLOR__GRAY,
});

interface Props {
  txnType: string;
  paymentType: string;
}

export const TransactionTypeLabel: FC<Props> = ({txnType, paymentType}) => {
  if (!txnType && !paymentType) {
    return null;
  }

  const {label, bgColor, textColor} =
    txnType2Attrs[txnType] ??
    (paymentType ? getPaymentAttrs(paymentType) : getFallbackAttrs(txnType));

  return (
    <ColorLabel display="inline-block" bgcolor={bgColor} color={textColor}>
      {label}
    </ColorLabel>
  );
};
